import { Hotel, HotelReview } from "typings/microserviceModels/hotels-flex-module";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { UitkCardSectionSeparatorType, UitkCardTextSegment } from "uitk-react-cards";
import TruncatedText from "shiitake";
import * as React from "react";
import { LocalizedComponentClass, LocalizedText, withLocalization } from "bernie-l10n";
import { ReviewCountAndRating } from "./ReviewCountAndRating";
import { TravelerReviewsContent } from "components/flexComponents/Hotels/components/TravelerReviewsContent";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { useLocalization } from "@shared-ui/localization-context";

interface FeaturedReviewContentProps {
  hotel: Hotel;
  context: ExtendedContextStore;
  showGuestRating?: boolean;
}

function showReview(featuredReviews?: HotelReview[]) {
  return featuredReviews && featuredReviews.length > 0;
}

export function showReviewCountAndRating(
  showGuestRating?: boolean,
  reviewCount?: number,
  hotelOverallReviewRating?: number,
  featuredReviews?: HotelReview[]
) {
  return (
    (showGuestRating || (showGuestRating == null && showReview(featuredReviews))) &&
    (reviewCount || reviewCount === 0 || hotelOverallReviewRating || hotelOverallReviewRating === 0)
  );
}

export const FeaturedReviewComponent = (props: FeaturedReviewContentProps) => {
  const { featuredReviews, reviewCount, hotelOverallReviewRating, travelerReviewContent } = props.hotel;

  const hasReviews = featuredReviews && showReview(featuredReviews);

  if (
    !hasReviews &&
    !showReviewCountAndRating(props.showGuestRating, reviewCount, hotelOverallReviewRating, featuredReviews)
  ) {
    return null;
  }

  const isIncreaseAffinityRelevanceExperimentEnabled = isVariantEnabled(
    props.context,
    Experiment.BEX_TGFH_Increase_Affinity_Relevance
  );
  const isAffinityReviewExpEnabled = isVariantEnabled(props.context, Experiment.Flex_HFTG_Affinity_Reviews);

  const hasReviewTitle = featuredReviews && featuredReviews[0].title;
  const showReviewDefaultTitle =
    !hasReviewTitle &&
    ((isIncreaseAffinityRelevanceExperimentEnabled && travelerReviewContent) || isAffinityReviewExpEnabled);

  const { formatText } = useLocalization();
  const language = props.context.locale?.split(/[_-]/);

  return (
    <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP}>
      {showReviewCountAndRating(props.showGuestRating, reviewCount, hotelOverallReviewRating, featuredReviews) && (
        <ReviewCountAndRating
          hotelOverallReviewRating={props.hotel.hotelOverallReviewRating}
          reviewCount={props.hotel.reviewCount}
          language={language}
        />
      )}

      {isIncreaseAffinityRelevanceExperimentEnabled && (
        <>
          <TravelerReviewsContent travelerReviewContent={travelerReviewContent} hotelId={props.hotel.hotelId} />
        </>
      )}
      {hasReviews && showReviewDefaultTitle && (
        <div className="uitk-type-bold uitk-type-300 all-r-margin-one all-t-padding-three hotelRating">
          {formatText("hotels.travelers.say")}
        </div>
      )}

      {hasReviews && hasReviewTitle && (
        <div className="uitk-type-heading-300 all-b-margin-two">{featuredReviews[0].title}</div>
      )}
      {hasReviews && (
        <TruncatedText
          lines={3}
          renderFullOnServer={true}
          overflowNode={"\u2026\u0022"}
          className="truncatedReview uitk-type-paragraph-300"
          tagName="p"
        >
          {`\u0022${featuredReviews[0].review}\u0022`}
        </TruncatedText>
      )}
      {hasReviews && (
        <p className="uitk-type-paragraph-200">
          <LocalizedText message="hotels.review.reviewedOn" data={[featuredReviews[0].reviewDate]} />
        </p>
      )}
    </UitkCardTextSegment>
  );
};

export const FeaturedReviewContent: LocalizedComponentClass<FeaturedReviewContentProps> = withLocalization(
  FeaturedReviewComponent
);
